import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { NewsletterForm } from "../components/NewsletterForm";
import { ProductCard } from "../components/ProductCard";
import { ConnectState } from "../components/ConnectState";
import { useProducts } from "../lib/api";

const HERO = "/images/hero-model.png";

const SCHEDULE = [
  { city: "San Francisco", day: "Tuesdays", time: "21:00", meet: "Embarcadero, Pier 7" },
  { city: "New York", day: "Thursdays", time: "20:30", meet: "Williamsburg Bridge, Manhattan side" },
  { city: "Tokyo", day: "Saturdays", time: "21:00", meet: "Yoyogi Park, south gate" },
];

export default function RunClub() {
  const gear = useProducts({ first: 4, q: "run" });

  return (
    <div>
      <div className="relative flex min-h-[70svh] items-end overflow-hidden border-b border-border">
        <img src={HERO} alt="TRAILNONE Run Club night run" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />
        <div className="relative z-10 w-full px-5 pb-14 pt-32 sm:px-10 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-signal"
          >
            TRAILNONE Run Club — 001
          </motion.p>
          <h1 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.85] tracking-tighter sm:text-6xl lg:text-7xl">
            Run your
            <br />
            <span className="text-outline">own route.</span>
          </h1>
        </div>
      </div>

      <section className="grid gap-10 px-5 py-16 sm:px-10 lg:grid-cols-2 lg:px-16 lg:py-24">
        <Reveal>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
            No pace. No egos. Just miles.
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
            The Run Club is TRAILNONE's beating heart — a night crew that
            meets weekly, runs the city, and earns first access to limited
            drops and reflective colorways. Show up in whatever you have.
            Leave with a crew.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border border-border p-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-signal">
              Join the crew
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Get the weekly route, start location and member drop codes.
            </p>
            <div className="mt-6">
              <NewsletterForm source="run-club" />
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-border px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
        <Reveal>
          <h2 className="mb-10 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
            Weekly runs
          </h2>
        </Reveal>
        <div className="grid gap-px border border-border bg-border lg:grid-cols-3">
          {SCHEDULE.map((s, i) => (
            <div key={s.city} className="bg-background p-8" data-testid={`run-schedule-${s.city.toLowerCase().replace(/\s+/g, "-")}`}>
              <Reveal delay={i * 0.08}>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">
                  {String(i + 1).padStart(3, "0")}
                </p>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight">
                  {s.city}
                </h3>
                <div className="mt-4 space-y-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  <p>{s.day} — {s.time}</p>
                  <p className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-signal" /> {s.meet}
                  </p>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
        <Reveal>
          <h2 className="mb-10 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
            Runner kit
          </h2>
        </Reveal>
        {gear.loading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-secondary" />
            ))}
          </div>
        ) : !gear.connected && gear.products.length === 0 ? (
          <ConnectState compact />
        ) : gear.products.length === 0 ? (
          <p className="border border-border p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Tag products "run" in Shopify to feature them here
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {gear.products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
